import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MessageSquare, AppWindow, ListTodo, FileBox, PanelLeftClose, PanelLeft } from "lucide-react";
import { useSidebarCollapsed, useSidebarWidth } from "@/hooks/use-sidebar";
import { cn } from "@/lib/utils";

export function CollapsibleSidebar({ children }: { children: React.ReactNode }) {
  const { collapsed, toggle } = useSidebarCollapsed();
  const { width, setWidth, reset } = useSidebarWidth();
  const [isDragging, setIsDragging] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: PointerEvent) => {
      // Sidebar is pinned to the left edge — width grows as the pointer moves right.
      setWidth(e.clientX);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isDragging, setWidth]);

  // Extract tenantId from URL for icon nav links
  const match = location.pathname.match(/\/tenants\/([^/]+)/);
  const tenantId = match?.[1];
  const base = tenantId ? `/tenants/${tenantId}` : "";

  const iconNav = [
    { to: `${base}/workspace`, icon: MessageSquare, label: "Chats" },
    { to: `${base}/apps`, icon: AppWindow, label: "Apps" },
    { to: `${base}/tasks`, icon: ListTodo, label: "Tasks" },
    { to: `${base}/artifacts`, icon: FileBox, label: "Artifacts" },
  ];

  return (
    <div className="relative shrink-0 group">
      <div
        className={cn(
          "h-full overflow-hidden",
          // Skip width transition while drag-resizing — it would lag the pointer.
          !(isDragging && !collapsed) && "transition-all duration-200 ease-in-out",
          // w-[50px] when collapsed reserves real flex width so <main>
          // can't capture clicks meant for the mini sidebar.
          collapsed && "w-[50px]",
        )}
        style={collapsed ? undefined : { width }}
      >
        {collapsed ? (
          <div className="flex h-full w-[50px] flex-col items-center border-r border-border bg-sidebar pt-3 pb-2 gap-1">
            <button
              onClick={toggle}
              className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg bg-accent hover:bg-accent-hover transition-colors cursor-pointer"
              title="Expand sidebar"
            >
              <span className="text-[13px] font-bold text-white">P</span>
            </button>

            <div className="mx-2 mb-1 h-px w-6 bg-border" />

            {base && iconNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
                    isActive
                      ? "bg-[#ece9e3] text-text-primary dark:bg-[#3a3a3a]"
                      : "text-text-muted hover:bg-surface-hover hover:text-text-secondary",
                  )
                }
                title={item.label}
              >
                <item.icon size={18} strokeWidth={1.8} />
              </NavLink>
            ))}

            <div className="mt-auto">
              <button
                onClick={toggle}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-hover hover:text-text-primary transition-colors"
                title="Expand sidebar"
              >
                <PanelLeft size={16} />
              </button>
            </div>
          </div>
        ) : (
          children
        )}
      </div>

      {/* Drag handle straddling the right edge — only when expanded.
          Wider than visually needed (10px) so it's easy to grab anywhere
          along the full height of the sidebar. */}
      {!collapsed && (
        <div
          onPointerDown={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDoubleClick={reset}
          className={cn(
            "absolute top-0 bottom-0 w-[10px] cursor-col-resize z-10",
            "after:content-[''] after:absolute after:top-0 after:bottom-0 after:left-1/2",
            "after:-translate-x-1/2 after:w-px after:bg-transparent after:transition-colors",
            "hover:after:bg-accent/60",
            isDragging && "after:bg-accent",
          )}
          style={{ left: width - 5 }}
          title="Drag to resize · double-click to reset"
        />
      )}

      {!collapsed && (
        <button
          onClick={toggle}
          className="absolute z-20 flex h-7 w-7 items-center justify-center rounded-md text-text-muted opacity-0 group-hover:opacity-70 hover:!opacity-100 hover:bg-surface-hover transition-all"
          style={{ right: "8px", top: "18px" }}
          title="Collapse sidebar"
        >
          <PanelLeftClose size={16} />
        </button>
      )}
    </div>
  );
}
